import React, { useState, useEffect, useRef } from "react";
import {
  Flex,
  Box,
  Avatar,
  Button,
  Text,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useColorModeValue,
  Spinner,
  Grid,
  Icon,
  Heading,
  Badge,
  HStack,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { getToken, getRole } from "utils/auth";
import config from "config";
import ProfileBgImage from "assets/img/ProfileBackground.png";
import { CheckCircleIcon, EditIcon } from "@chakra-ui/icons";
import { FaUserCheck, FaEnvelope, FaBriefcase, FaUserEdit, FaShieldAlt } from "react-icons/fa";

function Profile() {
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const inputBg = useColorModeValue("slate.50", "navy.800");
  const toast = useToast();

  const [profileInfo, setProfileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const funcRef = useRef();
  const descriptionRef = useRef();

  useEffect(() => {
    const fetchProfileInfo = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${config.API_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfileInfo(response.data);
      } catch (err) {
        setError("Failed to load profile information");
      } finally {
        setLoading(false);
      }
    };
    fetchProfileInfo();
  }, []);

  const handleSave = async () => {
    try {
      const updatedProfile = {
        "first_name": firstNameRef.current.value,
        "last_name": lastNameRef.current.value,
        "function": funcRef.current.value,
        "description": descriptionRef.current.value,
      };

      const token = getToken();
      await axios.put(`${config.API_URL}/user/profile`, updatedProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: "Profile updated.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setIsEditing(false);
      setProfileInfo({ ...profileInfo, ...updatedProfile });
    } catch (error) {
      toast({
        title: "Error updating profile.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "62px", md: "75px", xl: "88px" }} gap="24px">
      {loading ? (
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" color="teal.500" thickness="4px" />
        </Flex>
      ) : error ? (
        <Box bg={cardBg} p="30px" borderRadius="20px" textAlign="center" border="1px solid" borderColor="red.300">
          <Text color="red.500" fontWeight="bold">{error}</Text>
        </Box>
      ) : (
        <Box bg={cardBg} borderRadius="24px" border="1px solid" borderColor={cardBorder} shadow="sm" overflow="hidden">
          {/* Header Banner */}
          <Box
            bgImage={ProfileBgImage}
            h="180px"
            bgSize="cover"
            bgPosition="center"
            position="relative"
          />

          <Box p={{ base: "14px", sm: "20px", md: "32px" }} pt="0" position="relative">
            <Flex direction={{ base: "column", md: "row" }} align={{ base: "center", md: "flex-end" }} justify="space-between" mt="-60px" mb="24px" gap="20px">
              <Flex direction={{ base: "column", md: "row" }} align="center" gap="16px" textAlign={{ base: "center", md: "left" }}>
                <Avatar
                  name={`${profileInfo?.first_name || ""} ${profileInfo?.last_name || ""}`}
                  size="2xl"
                  border="4px solid"
                  borderColor={cardBg}
                  shadow="lg"
                  bg="teal.500"
                  color="white"
                />
                <Box pt="10px">
                  <HStack spacing="10px" justify={{ base: "center", md: "flex-start" }} wrap="wrap">
                    <Heading size="lg" color={textColor} fontWeight="800">
                      {`${profileInfo?.first_name || "User"} ${profileInfo?.last_name || ""}`}
                    </Heading>
                    <Badge colorScheme="purple" borderRadius="full" px="10px" py="2px" textTransform="uppercase">
                      {getRole() || "Researcher"}
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" color={subtitleColor} mt="2px">
                    {profileInfo?.email || "No email provided"}
                  </Text>
                </Box>
              </Flex>

              {!isEditing && (
                <Button
                  colorScheme="teal"
                  leftIcon={<FaUserEdit />}
                  borderRadius="12px"
                  onClick={() => setIsEditing(true)}
                  px="20px"
                  w={{ base: "100%", sm: "auto" }}
                >
                  Edit Profile
                </Button>
              )}
            </Flex>

            {/* Profile Info Details or Form */}
            {isEditing ? (
              <Box p="24px" bg={inputBg} borderRadius="20px" border="1px solid" borderColor={cardBorder} mt="20px">
                <Heading size="sm" mb="18px" color={textColor}>Edit User Profile</Heading>
                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="18px" mb="24px">
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase">First Name</FormLabel>
                    <Input
                      ref={firstNameRef}
                      defaultValue={profileInfo?.first_name || ""}
                      borderRadius="12px"
                      bg={cardBg}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase">Last Name</FormLabel>
                    <Input
                      ref={lastNameRef}
                      defaultValue={profileInfo?.last_name || ""}
                      borderRadius="12px"
                      bg={cardBg}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase">Role / Position</FormLabel>
                    <Input
                      ref={funcRef}
                      defaultValue={profileInfo?.function || ""}
                      borderRadius="12px"
                      bg={cardBg}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase">Bio / Description</FormLabel>
                    <Input
                      ref={descriptionRef}
                      defaultValue={profileInfo?.description || ""}
                      borderRadius="12px"
                      bg={cardBg}
                    />
                  </FormControl>
                </Grid>
                <HStack spacing="12px">
                  <Button colorScheme="teal" borderRadius="12px" onClick={handleSave}>
                    Save Changes
                  </Button>
                  <Button variant="outline" borderRadius="12px" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </HStack>
              </Box>
            ) : (
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="20px" mt="20px">
                <Box p="20px" bg={inputBg} borderRadius="18px">
                  <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase" mb="4px">Role / Position</Text>
                  <Text fontSize="md" fontWeight="700" color={textColor}>
                    {profileInfo?.function || "Research Associate / Geneticist"}
                  </Text>
                </Box>
                <Box p="20px" bg={inputBg} borderRadius="18px">
                  <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase" mb="4px">Bio / Description</Text>
                  <Text fontSize="md" fontWeight="500" color={textColor}>
                    {profileInfo?.description || "Institut Pasteur de Tunis Xeroderma Pigmentosum Research Team Member."}
                  </Text>
                </Box>
              </Grid>
            )}
          </Box>
        </Box>
      )}
    </Flex>
  );
}

export default Profile;

